import { useEffect, type RefObject } from 'react'
import type { EChartsType } from 'echarts/core'
import type { EChartHandle } from '../../components/ui'

/**
 * Связывает перекрестия графиков профиля: наведение на день на одном графике показывает подсказку того же
 * дня на остальных. Оси у графиков общие (одни и те же даты), но экземпляр ECharts появляется после ленивой
 * загрузки движка, а переключатель «график / таблица» пересоздаёт его — поэтому привязка проверяется по таймеру.
 * Ничего не делает, пока график не готов; ошибки движка (график уже выгружен) гасятся.
 */
export function useLinkedCrosshair(refs: ReadonlyArray<RefObject<EChartHandle | null>>, days: readonly string[], enabled = true): void {
  useEffect(() => {
    if (!enabled) return undefined
    const bound = new Map<EChartsType, () => void>()
    let syncing = false

    const others = (self: EChartsType) => [...bound.keys()].filter((chart) => chart !== self)
    const indexOf = (value: unknown): number | null => {
      if (typeof value === 'number' && Number.isFinite(value)) return Math.round(value)
      const index = days.indexOf(String(value))
      return index >= 0 ? index : null
    }

    const bind = (chart: EChartsType) => {
      const onPointer = (event: any) => {
        if (syncing) return
        const index = indexOf(event?.axesInfo?.[0]?.value)
        if (index === null) return
        syncing = true
        try {
          for (const other of others(chart)) other.dispatchAction({ type: 'showTip', seriesIndex: 0, dataIndex: index })
        } catch {
          /* график выгружен между проверкой и вызовом */
        } finally {
          syncing = false
        }
      }
      const onLeave = () => {
        if (syncing) return
        syncing = true
        try {
          for (const other of others(chart)) other.dispatchAction({ type: 'hideTip' })
        } catch {
          /* см. выше */
        } finally {
          syncing = false
        }
      }
      chart.on('updateAxisPointer', onPointer)
      chart.getZr().on('globalout', onLeave)
      bound.set(chart, () => {
        try {
          chart.off('updateAxisPointer', onPointer)
          chart.getZr().off('globalout', onLeave)
        } catch {
          /* экземпляр уже выгружен */
        }
      })
    }

    const sync = () => {
      const alive = new Set<EChartsType>()
      for (const ref of refs) {
        const chart = ref.current?.getInstance() ?? null
        if (!chart) continue
        alive.add(chart)
        if (!bound.has(chart)) bind(chart)
      }
      for (const [chart, unbind] of bound) {
        if (!alive.has(chart)) {
          unbind()
          bound.delete(chart)
        }
      }
    }

    sync()
    const timer = setInterval(sync, 400)
    return () => {
      clearInterval(timer)
      for (const unbind of bound.values()) unbind()
      bound.clear()
    }
    // refs — стабильные объекты ref; даты общие для всех графиков.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days, enabled])
}
